import * as React from "react"
import ReactDOM from "react-dom"
import { type Category, type EntityCategory } from "@digits-graphql/frontend/graphql-bearer"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgPlus } from "@digits-shared/components/SVGIcons/line/Plus.svg"
import { Modal, ModalCenterPositioning } from "@digits-shared/components/UI/Elements/Modals/Modal"
import { DigitsButton } from "@digits-shared/DesignSystem/Button"
import { DigitsTooltip } from "@digits-shared/DesignSystem/Tooltip"
import { useEffectOnce } from "@digits-shared/hooks/useEffectOnce"
import { useModalRoot } from "@digits-shared/hooks/useModalRoot"
import colors from "@digits-shared/themes/colors"
import { H2Text } from "@digits-shared/themes/typography"
import { useMolecule } from "bunshi/react"
import { useAtomValue, useSetAtom } from "jotai"
import styled from "styled-components"
import { CategoryDescriptionTextArea } from "src/frontend/components/OS/Applications/CategoryManager/CategoryPanel/Inputs/CategoryDescriptionTextArea"
import { CategoryIconSelect } from "src/frontend/components/OS/Applications/CategoryManager/CategoryPanel/Inputs/CategoryIconSelect"
import { CategoryNameInput } from "src/frontend/components/OS/Applications/CategoryManager/CategoryPanel/Inputs/CategoryNameInput"
import { CategoryNumberInput } from "src/frontend/components/OS/Applications/CategoryManager/CategoryPanel/Inputs/CategoryNumberInput"
import { ParentCategorySelect } from "src/frontend/components/OS/Applications/CategoryManager/CategoryPanel/Inputs/ParentCategorySelect"
import { SubtypeSelect } from "src/frontend/components/OS/Applications/CategoryManager/CategoryPanel/Inputs/SubtypeSelect"
import { newCategoryModalMolecule } from "src/frontend/components/OS/Applications/CategoryManager/CategoryPanel/NewCategoryModal/newCategoryModalMolecule"
import { NewCategoryModalScopeProvider } from "src/frontend/components/OS/Applications/CategoryManager/CategoryPanel/NewCategoryModal/NewCategoryModalScope"
import { useCreateOrUpdateCategory } from "src/frontend/components/OS/Applications/CategoryManager/CategoryPanel/NewCategoryModal/useCreateOrUpdateCategory"
import { useHydrateExistingCategoryData } from "src/frontend/components/OS/Applications/CategoryManager/CategoryPanel/NewCategoryModal/useHydrateExistingCategoryData"
import {
  InputRow,
  PanelFooter,
  PanelHeader,
  PanelSections,
  PanelTitle,
} from "src/frontend/components/OS/Applications/CategoryManager/CategoryPanel/Shared"
import { type NewCategoryEntity } from "src/shared/components/Typeahead/sharedTypeahead"

export const ModalContainer = styled.div`
  position: relative;
  width: 360px;
  border-radius: 16px;
  background: ${colors.translucentWhite50};
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 74px;
    background: ${colors.teal};
    border-radius: 16px 16px 0 0;
  }
`

const ModalSection = styled.div`
  position: relative;
  background: ${colors.white};
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const NewCategoryIcon = styled(SvgPlus)`
  height: 24px;
  width: 24px;
  ${svgPathStyles(colors.white, 1.5)};
`

interface NewCategoryModalProps {
  initialCategory?: Category | NewCategoryEntity
  onClose: (categoryId: string | undefined) => void
  onCreateNew: (entity: EntityCategory) => void
}

export const NewCategoryModal: React.FC<NewCategoryModalProps> = ({
  onClose,
  onCreateNew,
  initialCategory,
}) => {
  const root = useModalRoot()
  useHydrateExistingCategoryData()

  const {
    isValidAtom,
    initializeModalStateAtom,
    categoryNumberAtom,
    categoryNameAtom,
    parentCategoryAtom,
    iconDisplayKeyAtom,
    subtypeAtom,
    descriptionAtom,
    completeCategoryNumberAtom,
    existingCategoryNumbersAtom,
    originalCategoryNumberAtom,
    excludedParentCategoryIdsAtom,
    isCategoryNameUniqueAtom,
  } = useMolecule(newCategoryModalMolecule)

  const { createOrUpdateCategory, submitting } = useCreateOrUpdateCategory(onClose, onCreateNew)
  const isValid = useAtomValue(isValidAtom)
  const categoryName = useAtomValue(categoryNameAtom)
  const parentCategory = useAtomValue(parentCategoryAtom)
  const completeCategoryNumber = useAtomValue(completeCategoryNumberAtom)
  const originalCategoryNumber = useAtomValue(originalCategoryNumberAtom)
  const excludedParentCategoryIds = useAtomValue(excludedParentCategoryIdsAtom)
  const initializeModalState = useSetAtom(initializeModalStateAtom)
  const isCategoryNameUnique = useAtomValue(isCategoryNameUniqueAtom)

  useEffectOnce(() => {
    initializeModalState(initialCategory)
  })

  const onModalClose = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onClose(undefined)
    },
    [onClose]
  )

  return ReactDOM.createPortal(
    <NewCategoryModalScopeProvider>
      <Modal useCustomModalContainer preventModalClickPropagation>
        <ModalCenterPositioning>
          <ModalContainer>
            <PanelHeader>
              <PanelTitle>
                <NewCategoryIcon />
                <H2Text color={colors.white}>New Category</H2Text>
              </PanelTitle>
            </PanelHeader>

            <PanelSections>
              <ModalSection>
                <CategoryNameInput
                  categoryNameAtom={categoryNameAtom}
                  isNameUnique={isCategoryNameUnique}
                />

                <ParentCategorySelect
                  parentCategoryAtom={parentCategoryAtom}
                  subtypeAtom={subtypeAtom}
                  excludedCategoryIds={excludedParentCategoryIds}
                />

                <DigitsTooltip content="Select parent first" disabled={Boolean(parentCategory)}>
                  <SubtypeSelect
                    parentCategoryAtom={parentCategoryAtom}
                    subtypeAtom={subtypeAtom}
                  />
                </DigitsTooltip>

                <InputRow>
                  <CategoryIconSelect
                    displayKeyAtom={iconDisplayKeyAtom}
                    subtypeAtom={subtypeAtom}
                    parentCategoryAtom={parentCategoryAtom}
                    disabled={!categoryName || !parentCategory}
                  />

                  <CategoryNumberInput
                    categoryNumberAtom={categoryNumberAtom}
                    categoryNumbersAtom={existingCategoryNumbersAtom}
                    parentCategoryType={parentCategory?.type}
                    completeCategoryNumber={completeCategoryNumber}
                    disabled={!categoryName || !parentCategory}
                    originalCategoryNumber={originalCategoryNumber}
                    strict
                  />
                </InputRow>

                <CategoryDescriptionTextArea descriptionAtom={descriptionAtom} />
              </ModalSection>
            </PanelSections>

            <PanelFooter>
              <DigitsButton onClick={onModalClose} $variant="secondary-dark">
                Cancel
              </DigitsButton>

              <DigitsButton
                disabled={!isValid}
                onClick={createOrUpdateCategory}
                loading={submitting}
              >
                Create
              </DigitsButton>
            </PanelFooter>
          </ModalContainer>
        </ModalCenterPositioning>
      </Modal>
    </NewCategoryModalScopeProvider>,
    root
  )
}
